<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h4 class="mb-0">Contracts</h4>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-dark" @click="exportToExcel">
            <i class="fas fa-file-excel me-1"></i> Export
          </button>
          <router-link to="/add-contract">
            <button class="btn text-white" style="background-color: #4b5563;">+ Add Contract</button>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Title bar + Search -->
    <div class="row mb-4">
      <div class="col-12">
        <div
          class="card-header p-3 position-relative mt-n4 mx-3 z-index-2 d-flex justify-content-between align-items-center"
          style="background: linear-gradient(195deg, #4b5563, #8d2e38);; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"
        >
          <h6 class="text-white text-capitalize m-0">Contracts Table</h6>
          <div class="search-bar bg-white rounded d-flex align-items-center px-2" style="height: 40px; width: 280px;">
            <input
              v-model="searchTerm"
              type="text"
              class="form-control border-0 shadow-none"
              placeholder="Search by reference or name"
              style="font-size: 0.875rem;"
            />
            <i class="fas fa-search text-secondary"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-body px-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Start Date</th>
                    <th>Status</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="contract in filteredContracts" :key="contract.id">
                    <td>{{ contract.reference }}</td>
                    <td>{{ contract.user?.firstname }} {{ contract.user?.lastname }}</td>
                    <td>{{ contract.contract_type?.name }}</td>
                    <td>{{ formatDate(contract.start_date) }}</td>
                    <td>
                      <span :class="contract.is_active ? 'text-success' : 'text-danger'">
                        {{ contract.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="text-center">
                      <router-link :to="`/edit-contract/${contract.id}`" class="text-primary me-3">
                        <i class="fas fa-edit"></i>
                      </router-link>

                      <a href="#" class="text-info me-3" @click.prevent="downloadContract(contract.user_id)">
                        <i class="fas fa-download"></i>
                      </a>

                      <a href="#" class="text-danger me-3" @click.prevent="deleteContract(contract.id)">
                        <i class="fas fa-trash-alt"></i>
                      </a>

                      <a href="#" class="text-dark" @click.prevent="toggleStatus(contract.id)">
                        <i class="fas fa-exchange-alt"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="filteredContracts.length === 0" class="text-center py-4 text-muted">
                No contracts found.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import * as XLSX from 'xlsx'

const contracts = ref([])
const searchTerm = ref('')
const toast = useToast()

const fetchContracts = async () => {
  try {
    const res = await axios.get('/employee/contracts')
    contracts.value = res.data?.data ?? []
  } catch (err) {
    toast.error('Error loading contracts')
  }
}

const deleteContract = async (id) => {
  if (confirm('Are you sure?')) {
    await axios.delete(`/employee/contracts/${id}`)
    toast.success('Contract deleted')
    fetchContracts()
  }
}

const toggleStatus = async (id) => {
  await axios.patch(`/employee/contracts/change-status/${id}`)
  toast.success('Status changed')
  fetchContracts()
}

const downloadContract = async (userId) => {
  try {
    toast.info('Téléchargement en cours...')
    const response = await axios.get(`/contracts/download/${userId}`, { responseType: 'blob' })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = fileURL
    link.setAttribute('download', `contrat_utilisateur_${userId}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(fileURL)
    toast.success('Téléchargement réussi')
  } catch (err) {
    console.error(err)
    toast.error(err.response?.status === 404 ? 'Document non trouvé' : 'Erreur de téléchargement')
  }
}

/* ✅ Recherche simple par référence + prénom/nom (case-insensitive) */
const filteredContracts = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()
  if (!term) return contracts.value
  return contracts.value.filter(c => {
    const ref = (c.reference ?? '').toLowerCase()
    const first = (c.user?.firstname ?? '').toLowerCase()
    const last  = (c.user?.lastname ?? '').toLowerCase()
    const full  = `${first} ${last}`.trim()
    return ref.includes(term) || first.includes(term) || last.includes(term) || full.includes(term)
  })
})

/* ✅ Date simple YYYY-MM-DD */
const formatDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  // fallback si chaîne déjà ISO
  return String(value).slice(0, 10)
}

/* ✅ Export propre (sans KaTeX), avec dates simples */
const exportToExcel = () => {
  const data = filteredContracts.value.map(c => ({
    Reference: c.reference,
    Employee: `${c.user?.firstname || ''} ${c.user?.lastname || ''}`.trim(),
    ContractType: c.contract_type?.name || '',
    StartDate: formatDate(c.start_date),
    EndDate: formatDate(c.end_date),
    Status: c.is_active ? 'Active' : 'Inactive'
  }))

  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Contracts')
  XLSX.writeFile(wb, 'contracts.xlsx')
}

onMounted(fetchContracts)
</script>
